import { type ReactNode, useMemo } from 'react'
import {
  Configure,
  InstantSearch as InstantSearchProvider
} from 'react-instantsearch'
import { liteClient as search } from 'algoliasearch/lite'

import { useUserResources } from 'shared/hooks'
import { useSearchParams } from 'shared/hooks/useSearchParams'

import { insightsConfig } from './lib/algolia'
import { entry, entryCategory } from './routes/proxy'
import { useAnalyticsTags } from './hooks/useAnalyticsTags'
import { IS_CATEGORY } from './utils/constants'
import { AlgoliaConfigProps, CategoryFiltersLabelProps } from './routes/routes'
import { algoliaStringFormatter } from './routes/proxy/category/utils/algoliaStringFormatter'
import { getFilters } from './utils/getFilters'

interface InstantSearchProps {
  children?: ReactNode
  isSearchResultPage?: boolean
  categoryPageId?: string
  algolia: AlgoliaConfigProps
  isAlgoliaAssistedSaleEnabled?: boolean
}

const HITS_PER_PAGE = 36
const MAX_VALUES_PER_FACET = 1000
const STALLED_SEARCH_DELAY = 2000

export function InstantSearch(props: InstantSearchProps) {
  const { children, categoryPageId, algolia, isAlgoliaAssistedSaleEnabled } =
    props

  const {
    userResources,
    userControls: { isAssistedSale }
  } = useUserResources()

  const query = useSearchParams()
  const isAlgoliaAssistedSale = isAssistedSale && !!isAlgoliaAssistedSaleEnabled

  const analyticsTags = useAnalyticsTags()

  const searchClient = useMemo(
    () => search(algolia.appId, algolia.apiKey),
    [algolia.appId, algolia.apiKey]
  )

  const customSearchClient = useMemo(() => {
    let cachedKey: string | null = null
    let cachedResult: ReturnType<typeof searchClient.search> | null = null
    let cacheTimer: ReturnType<typeof setTimeout> | null = null

    return {
      ...searchClient,
      search(
        ...args: Parameters<typeof searchClient.search>
      ): ReturnType<typeof searchClient.search> {
        const requests = args[0] as Array<{
          indexName: string
          params?: Record<string, unknown>
        }>

        if (requests.every(({ params }) => !params?.query)) {
          return Promise.resolve({
            results: requests.map(() => ({
              hits: [],
              nbHits: 0,
              nbPages: 0,
              page: 0,
              processingTimeMS: 0,
              hitsPerPage: 0,
              exhaustiveNbHits: false,
              query: '',
              params: ''
            }))
          }) as ReturnType<typeof searchClient.search>
        }

        const requestKey = JSON.stringify(
          requests.map(({ indexName, params }) => ({
            indexName,
            query: params?.query,
            page: params?.page,
            filters: params?.filters,
            facetFilters: params?.facetFilters
          }))
        )

        if (requestKey === cachedKey && cachedResult) {
          return cachedResult
        }

        if (cacheTimer) {
          clearTimeout(cacheTimer)
        }

        cachedKey = requestKey
        cachedResult = searchClient.search(...args)

        cachedResult.then(() => {
          cacheTimer = setTimeout(() => {
            cachedKey = null
            cachedResult = null
            cacheTimer = null
          }, 1000)
        })

        return cachedResult
      }
    } as typeof searchClient
  }, [searchClient])

  const region = userResources?.region
  const storeSlug = userResources?.storeSlug

  if (!region) {
    return null
  }

  const actualIndexName = isAlgoliaAssistedSale
    ? algolia.assistedSaleProductIndexName
    : algolia.indexName

  const filters = getFilters({
    isAssistedSale: isAlgoliaAssistedSale,
    storeSlug,
    region,
    categoryPageId
  })

  const categoryFiltersLabel = Object.keys(algolia.facets || {}).reduce(
    (acc: CategoryFiltersLabelProps, item: string) => {
      const key = algoliaStringFormatter(item)
      acc[key] = item
      return acc
    },
    {}
  )

  return (
    <InstantSearchProvider
      insights={insightsConfig}
      indexName={actualIndexName}
      initialUiState={{
        [actualIndexName]: {
          query: query.term
        }
      }}
      routing={
        IS_CATEGORY
          ? entryCategory(
              algolia.indexName,
              region,
              categoryFiltersLabel,
              algolia.facets
            )
          : entry(actualIndexName)
      }
      searchClient={IS_CATEGORY ? searchClient : customSearchClient}
      future={{
        preserveSharedStateOnUnmount: true
      }}
      stalledSearchDelay={STALLED_SEARCH_DELAY}>
      {region && (
        <Configure
          facets={['*']}
          filters={filters}
          hitsPerPage={HITS_PER_PAGE}
          analyticsTags={analyticsTags}
          maxValuesPerFacet={MAX_VALUES_PER_FACET}
          page={Math.max(Number(query.page) - 1, 0) || 0}
          analytics
          clickAnalytics
          facetingAfterDistinct
        />
      )}
      {children}
    </InstantSearchProvider>
  )
}
